import httpClient from "@utils/httpClient";

export interface Assessment {
  assessmentId: number;
  assessmentTitle: string;
  course: string;
  goal: string;
  startTime: string;
  endTime: string;
  status: string;
}

export interface EligibilityUser {
  id: number;
  assessment_id: number;
  user_id: number;
  user_email: string;
  user_name: string;
  uploaded_at: string;
}

export interface EligibilityData {
  assessmentId: number;
  totalUsers: number;
  users: EligibilityUser[];
}

export interface EligibilityStats {
  totalAssessments: number;
  assessmentsWithEligibility: number;
  totalEligibleUsers: number;
}

export interface ApiResponse<T = any> {
  success: boolean;
  status?: string;
  data?: T;
  message?: string;
  error?: string | { message: string };
  timestamp?: string;
}

/**
 * Get all assessments for eligibility management
 */
export const getAllAssessments = async (): Promise<ApiResponse<Assessment[]>> => {
  try {
    const response = await httpClient.get('/assessment-eligibility/assessments');
    return {
      success: true,
      data: response.data,
      message: response.message
    };
  } catch (error: any) {
    console.error('getAllAssessments error:', error);
    return {
      success: false,
      data: [],
      error: error.message || 'Failed to fetch assessments'
    };
  }
};

/**
 * Upload eligibility file (CSV or Excel) for an assessment
 */
export const uploadEligibilityFile = async (
  assessmentId: number,
  file: File
): Promise<ApiResponse> => {
  try {
    const formData = new FormData();
    formData.append('assessmentId', assessmentId.toString());
    formData.append('file', file);

    const response = await httpClient.post('/assessment-eligibility/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return {
      success: true,
      data: response.data,
      message: response.message || 'File uploaded successfully'
    };
  } catch (error: any) {
    console.error('uploadEligibilityFile error:', error);
    return {
      success: false,
      error: error.message || 'Failed to upload file'
    };
  }
};

/**
 * Get eligibility data for a specific assessment
 */
export const getAssessmentEligibility = async (
  assessmentId: number
): Promise<ApiResponse<EligibilityData>> => {
  try {
    const response = await httpClient.get(`/assessment-eligibility/assessment/${assessmentId}`);
    return {
      success: true,
      data: response.data
    };
  } catch (error: any) {
    console.error('getAssessmentEligibility error:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch eligibility data'
    };
  }
};

/**
 * Delete eligibility restrictions for an assessment
 */
export const deleteAssessmentEligibility = async (
  assessmentId: number
): Promise<ApiResponse> => {
  try {
    const response = await httpClient.delete(`/assessment-eligibility/assessment/${assessmentId}`);
    return {
      success: true,
      message: response.message || 'Eligibility deleted successfully'
    };
  } catch (error: any) {
    console.error('deleteAssessmentEligibility error:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete eligibility'
    };
  }
};

/**
 * Get eligibility statistics
 */
export const getEligibilityStats = async (): Promise<ApiResponse<EligibilityStats>> => {
  try {
    const response = await httpClient.get('/assessment-eligibility/stats');
    return {
      success: true,
      data: response.data
    };
  } catch (error: any) {
    console.error('getEligibilityStats error:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch statistics'
    };
  }
};

/**
 * Delete a specific user's eligibility for an assessment
 */
export const deleteUserEligibility = async (
  assessmentId: number,
  userId: number
): Promise<ApiResponse> => {
  try {
    const response = await httpClient.delete(`/assessment-eligibility/assessment/${assessmentId}/user/${userId}`);
    return {
      success: true,
      message: response.message || 'User eligibility removed successfully'
    };
  } catch (error: any) {
    console.error('deleteUserEligibility error:', error);
    return {
      success: false,
      error: error.message || 'Failed to remove user eligibility'
    };
  }
};
